import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Shield, FileText, CheckCircle, Clock, XCircle,
  AlertTriangle, Loader2, Download, Eye, RefreshCw, Search,
  User, Calendar, History
} from "lucide-react";
import { API } from "@/App";

const statusConfig = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700", icon: Clock },
  verified: { label: "Verified", color: "bg-green-100 text-green-700", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
  expired: { label: "Expired", color: "bg-slate-100 text-slate-700", icon: AlertTriangle }
};

const documentTypeLabels = {
  passport: "Passport",
  driving_licence: "Driving Licence",
  proof_of_address: "Proof of Address"
};

export const AMLVerificationPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [allDocs, setAllDocs] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState({ expiring_soon: [], already_expired: [] });
  const [accessLogs, setAccessLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Verification modal
  const [verifyModal, setVerifyModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (activeTab === "pending") {
        const response = await axios.get(`${API}/aml/pending`, { headers });
        setPendingDocs(response.data.documents || []);
      } else if (activeTab === "all") {
        const response = await axios.get(`${API}/aml/all`, { headers });
        setAllDocs(response.data.documents || []);
      } else if (activeTab === "expiring") {
        const response = await axios.get(`${API}/aml/expiring`, { headers });
        setExpiringDocs(response.data);
      } else if (activeTab === "audit") {
        const response = await axios.get(`${API}/aml/access-log`, { headers });
        setAccessLogs(response.data.logs || []);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const openVerifyModal = (doc) => {
    setSelectedDoc(doc);
    setRejectionReason("");
    setVerifyModal(true);
  };

  const handleVerify = async (action) => {
    if (action === "reject" && !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    setProcessing(true);
    try {
      await axios.patch(
        `${API}/aml/${selectedDoc.document_id}/verify?action=${action}${action === "reject" ? `&rejection_reason=${encodeURIComponent(rejectionReason)}` : ""}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Document ${action === "verify" ? "verified" : "rejected"} successfully`);
      setVerifyModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process document");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/aml/file/${doc.document_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Document downloaded");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const filteredDocs = (docs) => {
    if (!searchTerm) return docs;
    const term = searchTerm.toLowerCase();
    return docs.filter(d => 
      d.user_name?.toLowerCase().includes(term) ||
      d.user_email?.toLowerCase().includes(term) ||
      d.document_type?.toLowerCase().includes(term)
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold font-outfit flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  AML Verification
                </h1>
                <p className="text-sm text-muted-foreground">Review and verify client identity documents</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{pendingDocs.length}</p>
              <p className="text-xs text-muted-foreground">Pending Review</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {allDocs.filter(d => d.status === "verified").length}
              </p>
              <p className="text-xs text-muted-foreground">Verified</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">
                {expiringDocs.total_expired || 0}
              </p>
              <p className="text-xs text-muted-foreground">Expired</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">
                {expiringDocs.total_expiring || 0}
              </p>
              <p className="text-xs text-muted-foreground">Expiring Soon</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              <Clock className="w-4 h-4 mr-2" />
              Pending ({pendingDocs.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              <FileText className="w-4 h-4 mr-2" />
              All Documents
            </TabsTrigger>
            <TabsTrigger value="expiring" data-testid="tab-expiring">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Expiring
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <History className="w-4 h-4 mr-2" />
              Audit Log
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          {(activeTab === "all" || activeTab === "pending") && (
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          )}

          {/* Pending Tab */}
          <TabsContent value="pending" className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredDocs(pendingDocs).length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">All caught up!</h3>
                  <p className="text-sm text-muted-foreground">
                    No documents pending verification
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredDocs(pendingDocs).map((doc) => (
                  <Card key={doc.document_id} data-testid={`pending-doc-${doc.document_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                            <User className="w-6 h-6 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium">{doc.user_name}</p>
                            <p className="text-sm text-muted-foreground">{doc.user_email}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <Badge variant="outline">
                                {documentTypeLabels[doc.document_type] || doc.document_type}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {doc.file_name}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(doc)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openVerifyModal(doc)}
                            data-testid={`verify-btn-${doc.document_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* All Documents Tab */}
          <TabsContent value="all" className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Document</TableHead>
                      <TableHead>Expiry/Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Verified By</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocs(allDocs).map((doc) => {
                      const status = statusConfig[doc.status] || statusConfig.pending;
                      return (
                        <TableRow key={doc.document_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{doc.user_name}</p>
                              <p className="text-xs text-muted-foreground">{doc.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {documentTypeLabels[doc.document_type] || doc.document_type}
                          </TableCell>
                          <TableCell>
                            {doc.expiry_date || doc.document_date || "-"}
                          </TableCell>
                          <TableCell>
                            <Badge className={status.color}>{status.label}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {doc.verified_by_name || "-"}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(doc)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          {/* Expiring Tab */}
          <TabsContent value="expiring" className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {expiringDocs.already_expired?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-red-600 mb-3 flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Expired Documents ({expiringDocs.already_expired.length})
                    </h3>
                    <div className="space-y-2">
                      {expiringDocs.already_expired.map((doc) => (
                        <Card key={doc.document_id} className="border-red-200 bg-red-50">
                          <CardContent className="p-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium">{doc.user_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {documentTypeLabels[doc.document_type]} - {doc.days_status}
                              </p>
                            </div>
                            <Badge className="bg-red-100 text-red-700">Expired</Badge>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {expiringDocs.expiring_soon?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-amber-600 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Expiring Soon ({expiringDocs.expiring_soon.length})
                    </h3>
                    <div className="space-y-2">
                      {expiringDocs.expiring_soon.map((doc) => (
                        <Card key={doc.document_id} className="border-amber-200 bg-amber-50">
                          <CardContent className="p-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium">{doc.user_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {documentTypeLabels[doc.document_type]} - {doc.days_status}
                              </p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-700">Expiring</Badge>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {(!expiringDocs.already_expired?.length && !expiringDocs.expiring_soon?.length) && (
                  <Card className="border-dashed">
                    <CardContent className="py-12 text-center">
                      <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                      <h3 className="font-medium text-slate-700 mb-1">All documents valid</h3>
                      <p className="text-sm text-muted-foreground">
                        No documents expiring soon
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : accessLogs.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No access logs</h3>
                  <p className="text-sm text-muted-foreground">
                    Document access will be logged here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Document</TableHead>
                      <TableHead>Document Owner</TableHead>
                      <TableHead>Accessed By</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accessLogs.map((log) => (
                      <TableRow key={log.log_id}>
                        <TableCell className="text-sm">
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {documentTypeLabels[log.document_type] || log.document_type}
                        </TableCell>
                        <TableCell>{log.document_owner_name}</TableCell>
                        <TableCell className="font-medium">{log.accessed_by_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.accessed_by_role}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {log.access_reason}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Verify Modal */}
      <Dialog open={verifyModal} onOpenChange={setVerifyModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Review Document</DialogTitle>
            <DialogDescription>
              Verify or reject this document for AML compliance
            </DialogDescription>
          </DialogHeader>

          {selectedDoc && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Client:</span>
                  <span className="font-medium">{selectedDoc.user_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Email:</span>
                  <span>{selectedDoc.user_email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Document:</span>
                  <span>{documentTypeLabels[selectedDoc.document_type]}</span>
                </div>
                {selectedDoc.expiry_date && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Expiry:</span>
                    <span>{selectedDoc.expiry_date}</span>
                  </div>
                )}
                {selectedDoc.document_date && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Document Date:</span>
                    <span>{selectedDoc.document_date}</span>
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleDownload(selectedDoc)}
              >
                <Eye className="w-4 h-4 mr-2" />
                View Document
              </Button>

              <div>
                <Label>Rejection Reason (if rejecting)</Label>
                <Textarea
                  placeholder="e.g., Document is blurry, wrong name, expired..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="destructive"
              onClick={() => handleVerify("reject")}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Reject
            </Button>
            <Button
              onClick={() => handleVerify("verify")}
              disabled={processing}
              data-testid="confirm-verify-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Verify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AMLVerificationPage;
